
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import { axiosInstance } from '../../../utility/apiInstance';
import { ICustomField } from './types/CustomFieldTypes';

interface Props {
    fields: ICustomField[];
    onSaved?: (updated: ICustomField[]) => void;
    contactOrganisation: string | "";
    trackingNumber: string | "";
    type: string | "";
}

export default function CustomFieldsPanel({
    fields: initialFields,
    onSaved,
    contactOrganisation,
    trackingNumber,
    type,
}: Props) {
    const [fields, setFields] = useState<ICustomField[]>(initialFields);
    const [editFields, setEditFields] = useState<ICustomField[]>(initialFields);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        setFields(initialFields);
        setEditFields(initialFields);
    }, [initialFields]);

    const handleChange = (index: number, value: string) => {
        const updated = [...editFields];
        updated[index] = { ...updated[index], value };
        setEditFields(updated);
        const fieldId = updated[index]._id;
        if (validationErrors[fieldId]) {
            setValidationErrors((prev) => {
                const next = { ...prev };
                delete next[fieldId];
                return next;
            });
        }
    };

    const handleSave = async () => {
        const newErrors: Record<string, string> = {};
        for (const field of editFields) {
            const isRequired =
                field.required ||
                (typeof field.customFieldId === 'object'
                    ? (field.customFieldId as any)?.required
                    : false);
            if (isRequired && (!field.value || field.value.trim() === '')) {
                newErrors[field._id] = `${field.label || field.key} is required`;
            }
        }
        if (Object.keys(newErrors).length > 0) {
            setValidationErrors(newErrors);
            return;
        }
        setValidationErrors({});
        try {
            setSaving(true);
            const payload = editFields.map((f) => ({ _id: f._id, value: f.value || '' }));
            await axiosInstance.put('/api/v1/custom-field/update', { data: payload, trackingNumber, type });
            toast.success('Custom fields updated');
            setFields(editFields);
            onSaved?.(editFields);
            setIsEditing(false);
        } catch {
            toast.error('Failed to update custom fields');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditFields(fields);
        setValidationErrors({});
        setIsEditing(false);
    };

    const getFieldType = (field: ICustomField): 'Date' | 'select' | 'boolean' | 'number' | 'Textbox' => {
        const formField = typeof field.customFieldId === 'object' ? (field.customFieldId as any) : null;
        const fieldTypeName: string =
            typeof formField?.fieldType === 'object' ? formField?.fieldType?.name || '' : '';
        const dropdownOptions: string[] = formField?.dropdownOptions || [];
        switch (fieldTypeName) {
            case 'Date':          return 'Date';
            case 'Yes/No':        return 'boolean';
            case 'Dropdown List': return 'select';
            case 'Number':        return 'number';
            case 'Text Box':      return 'Textbox';
            default:
                if (dropdownOptions.length > 0) return 'select';
                return 'Textbox';
        }
    };

    const renderEditInput = (field: ICustomField, index: number) => {
        const fieldType = getFieldType(field);
        const formField = typeof field.customFieldId === 'object' ? (field.customFieldId as any) : null;
        const dropdownOptions: string[] = formField?.dropdownOptions || [];
        const value = field.value || '';
        const hasError = !!validationErrors[field._id];
        const inputStyle: React.CSSProperties = {
            ...s.input,
            ...(hasError ? { borderColor: '#ef4444', background: '#fff5f5' } : {}),
        };

        switch (fieldType) {
            case 'boolean':
                return (
                    <select style={inputStyle} value={value} onChange={(e) => handleChange(index, e.target.value)}>
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                );
            case 'select':
                return (
                    <select style={inputStyle} value={value} onChange={(e) => handleChange(index, e.target.value)}>
                        <option value="">Select</option>
                        {dropdownOptions.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                );
            case 'Date':
                return <input style={inputStyle} type="date" value={value} onChange={(e) => handleChange(index, e.target.value)} />;
            case 'number':
                return (
                    <input
                        style={inputStyle}
                        type="number"
                        value={value}
                        placeholder=""
                        min="0"
                        max="999999999999"
                        onChange={(e) => { if (e.target.value.length <= 12) handleChange(index, e.target.value); }}
                    />
                );
            default:
                return (
                    <input
                        style={inputStyle}
                        type="text"
                        value={value}
                        placeholder=""
                        maxLength={100}
                        onChange={(e) => handleChange(index, e.target.value)}
                    />
                );
        }
    };

    const renderDisplayValue = (field: ICustomField) => {
        const value = field.value || '';
        const fieldType = getFieldType(field);
        if (!value) return <span style={{ ...s.val, ...s.emptyVal }}>—</span>;
        if (fieldType === 'Date') {
            try {
                const formatted = new Date(value).toLocaleDateString('en-GB', {
                    day: '2-digit', month: 'short', year: 'numeric',
                });
                return <span style={s.val}>{formatted}</span>;
            } catch {
                return <span style={s.val}>{value}</span>;
            }
        }
        return <span style={s.val}>{value}</span>;
    };

    return (
        <div style={s.panel}>

            {/* Customer Name */}
            {contactOrganisation && (
                <div style={s.orgSection}>
                    <span style={s.title}>Customer Name</span>
                    <div style={s.orgValue}>
                        <div style={s.orgAvatar}>
                            {contactOrganisation.charAt(0).toUpperCase()}
                        </div>
                        <span style={s.orgName}>{contactOrganisation}</span>
                    </div>
                </div>
            )}

            {/* Header */}
            <div style={s.header}>
                <span style={s.title}>Custom fields</span>
                {!isEditing && (
                    <button
                        style={{
                            ...s.editIconBtn,
                            ...(fields.length === 0 ? s.editIconBtnDisabled : {}),
                        }}
                        onClick={() => fields.length > 0 && setIsEditing(true)}
                        disabled={fields.length === 0}
                        title="Edit custom fields"
                    >
                        <EditIcon style={{ fontSize: 15 }} />
                    </button>
                )}
            </div>

            {/* Fields grid */}
            {fields.length === 0 ? (
                <p style={s.empty}>No custom fields</p>
            ) : (
                <div style={s.grid}>
                    {(isEditing ? editFields : fields).map((field, i) => {
                        const formField =
                            typeof field.customFieldId === 'object' ? (field.customFieldId as any) : null;
                        const label = field.label || field.key;
                        const isRequired = field.required || formField?.required || false;
                        return (
                            <div key={field._id} style={s.field}>
                                <span style={s.key}>
                                    {label}
                                    {isRequired && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                                </span>
                                {isEditing ? renderEditInput(field, i) : renderDisplayValue(field)}
                                {isEditing && validationErrors[field._id] && (
                                    <span style={s.errorMsg}>{validationErrors[field._id]}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Save / Cancel */}
            {isEditing && (
                <div style={s.footer}>
                    <button style={{ ...s.btn, ...s.btnPrimary }} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button style={{ ...s.btn, ...s.btnGhost }} onClick={handleCancel}>
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
}

const s: Record<string, React.CSSProperties> = {
    panel: { padding: '16px 20px', background: '#ffffff' },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '14px',
    },
    title: {
        fontSize: '11px',
        fontWeight: 500,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },

    // ── Edit icon button — matches TabActionLogo editIconBtn style ────────────
    editIconBtn: {
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '7px',
        cursor: 'pointer',
        padding: '5px 6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        outline: 'none',
        boxShadow: 'none',
        lineHeight: 1,
        transition: 'all 0.15s ease',
    },
    editIconBtnDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
        pointerEvents: 'none' as const,
    },

    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '10px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '10px 12px',
        background: '#fafafa',
        borderRadius: '8px',
        border: '0.5px solid #efefef',
        minWidth: 0,
        overflow: 'hidden',
    },
    key: {
        fontSize: '11px',
        fontWeight: 500,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    val: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#1a1a1a',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    emptyVal: { color: '#c0c0c0', fontWeight: 400 },
    empty: { color: '#c0c0c0', fontWeight: 400, fontSize: '13px' },
    input: {
        fontSize: '13px',
        fontWeight: 500,
        color: '#1a1a1a',
        background: '#fff',
        border: '0.5px solid #d1d5db',
        borderRadius: '6px',
        padding: '5px 8px',
        outline: 'none',
        width: '100%',
        maxWidth: '100%',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        appearance: 'auto' as any,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    errorMsg: { fontSize: '11px', color: '#ef4444', marginTop: '2px' },
    footer: { display: 'flex', gap: '8px', marginTop: '14px' },
    btn: {
        fontSize: '12px',
        fontWeight: 500,
        padding: '5px 14px',
        borderRadius: '8px',
        cursor: 'pointer',
        border: '0.5px solid #d1d5db',
        background: 'transparent',
        color: '#1a1a1a',
        fontFamily: 'inherit',
    },
    btnPrimary: { background: '#1a1a1a', color: '#fff', borderColor: 'transparent' },
    btnGhost: { color: '#9ca3af', borderColor: 'transparent' },
    orgSection: {
        paddingBottom: '14px',
        marginBottom: '14px',
        borderBottom: '0.5px solid #e8e8e8',
    },
    orgValue: { display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' },
    orgAvatar: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: '#f0f0f0',
        border: '0.5px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 500,
        color: '#555',
        flexShrink: 0,
    },
    orgName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};